import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDateAU } from '../../../shared/format';
import { QASkeleton } from '../components/QASkeleton';
import { QAEmpty } from '../components/QAEmpty';

/* ------------------------------------------------------------------ types */

interface FileNoteFinding {
  ruleKey: string | null;
  ruleSummary: string | null;
  severity: string | null;
  status: string | null;
}

interface FileNoteSection {
  heading?: string;
}

interface SessionRow {
  id: string;
  sourceApp: string;
  sourceReferenceId: string;
  submittedAt: string;
  clientNameRaw: string | null;
  meetingType: string | null;
  meetingDate: string | null;
  engagementStage: string | null;
  declarationName: string | null;
  declarationAt: string | null;
  skillVersion: number | null;
  finalSections: FileNoteSection[] | null;
  findingsSummary: FileNoteFinding[] | null;
  pdfUrl: string | null;
  status: string;
  dedupeOutcome: string | null;
}

interface ClientLite {
  id: string;
  firstName: string;
  surname: string;
  dateOfBirth: string | null;
  email1: string | null;
  suburb: string | null;
  postcode: string | null;
}

/* ----------------------------------------------------------------- labels */

const STAGE_LABEL: Record<string, string> = {
  first_meeting: 'First / discovery meeting',
  fact_find: 'Fact-find',
  strategy_discussion: 'Strategy discussion',
  implementation: 'Implementation',
  review: 'Review / annual review',
  ad_hoc: 'Ad-hoc',
};

const REASON_LABEL: Record<string, { label: string; cls: string }> = {
  unmatched: { label: 'No matching client', cls: 'bg-caution/20 text-caution' },
  ambiguous: { label: 'Multiple name matches', cls: 'bg-caution/20 text-caution' },
  no_name: { label: 'No client name supplied', cls: 'bg-rust/10 text-rust' },
  manual_link: { label: 'Manually linked', cls: 'bg-forest/20 text-forest' },
};

/* ------------------------------------------------------------------- page */

const TAB_DEFS = [
  { key: 'unlinked', label: 'Unlinked' },
  { key: 'dismissed', label: 'Dismissed' },
] as const;

export function UnlinkedFileNotes() {
  const [tab, setTab] = useState<'unlinked' | 'dismissed'>('unlinked');
  const qc = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['file-note-sessions-admin', tab],
    queryFn: () => api<{ data: SessionRow[] }>(`/api/admin/file-note-sessions?state=${tab}`),
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['file-note-sessions-admin'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const link = useMutation({
    mutationFn: ({ id, clientId }: { id: string; clientId: string }) =>
      api(`/api/admin/file-note-sessions/${id}/link`, {
        method: 'POST',
        body: JSON.stringify({ clientId }),
      }),
    onSuccess: refresh,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/file-note-sessions/${id}/dismiss`, { method: 'POST' }),
    onSuccess: refresh,
  });
  const restore = useMutation({
    mutationFn: (id: string) =>
      api(`/api/admin/file-note-sessions/${id}/restore`, { method: 'POST' }),
    onSuccess: refresh,
  });

  const busy = link.isPending || dismiss.isPending || restore.isPending;

  if (isLoading) return <QASkeleton variant="cards" />;
  if (isError || !data) return <div className="qa-badge qa-badge--out">Unable to load file note sessions</div>;

  return (
    <div className="py-6">
      <header className="mb-6">
        <h1 className="qa-h2">
          File notes without a <em className="text-rust">home.</em>
        </h1>
        <p className="text-ink-mute text-sm mt-2 max-w-2xl">
          File-Note.AI sends each finalised note here and matches it to a client on name alone. When
          the name doesn't resolve to exactly one client, the note waits here for triage.{' '}
          <strong>Link</strong> it to the right client, or <strong>Dismiss</strong> it if it isn't a
          client we hold. Linking stamps the client's file-note compliance date.
        </p>
      </header>

      <div className="qa-tabs mb-4">
        {TAB_DEFS.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`qa-tab ${tab === key ? 'qa-tab--active' : ''}`}
            aria-current={tab === key ? 'page' : undefined}
          >
            {label}
          </button>
        ))}
      </div>

      {data.data.length === 0 ? (
        <QAEmpty inline title={`No ${tab} file note sessions.`}>
          {tab === 'unlinked' && 'Everything coming in from File-Note.AI is matching cleanly.'}
        </QAEmpty>
      ) : (
        <ul className="space-y-4">
          {data.data.map((s) => (
            <SessionCard
              key={s.id}
              s={s}
              tab={tab}
              busy={busy}
              onLink={(clientId) => link.mutate({ id: s.id, clientId })}
              onDismiss={() => dismiss.mutate(s.id)}
              onRestore={() => restore.mutate(s.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- session card */

function SessionCard({
  s,
  tab,
  busy,
  onLink,
  onDismiss,
  onRestore,
}: {
  s: SessionRow;
  tab: 'unlinked' | 'dismissed';
  busy: boolean;
  onLink: (clientId: string) => void;
  onDismiss: () => void;
  onRestore: () => void;
}) {
  const [linking, setLinking] = useState(false);
  const reason = s.dedupeOutcome ? REASON_LABEL[s.dedupeOutcome] : null;
  const findings = s.findingsSummary ?? [];
  const sections = s.finalSections ?? [];
  const openFindings = findings.filter((f) => f.status === 'open').length;

  return (
    <li className="qa-card">
      <div className="flex items-center justify-between px-4 py-2 border-b border-hairline-paper text-xs qa-mono uppercase">
        <div className="flex items-center gap-3">
          <span className="text-ink-mute">submitted</span>
          <span className="text-ink">{formatDateAU(s.submittedAt)}</span>
          <span className="text-ink-mute">·</span>
          <span className="text-ink-mute">via</span>
          <span className="text-ink">{s.sourceApp}</span>
        </div>
        {reason && <span className={`px-2 py-0.5 ${reason.cls}`}>{reason.label}</span>}
      </div>

      <div className="p-4">
        <div className="text-xs qa-mono uppercase text-ink-mute mb-1">Client name as supplied</div>
        <h3 className="qa-display text-xl mb-3">
          {s.clientNameRaw ? s.clientNameRaw : <em className="text-ink-mute">(no name supplied)</em>}
        </h3>

        <dl className="text-sm grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
          {s.engagementStage && (
            <Row label="Engagement stage" v={STAGE_LABEL[s.engagementStage] ?? s.engagementStage} />
          )}
          {s.meetingType && <Row label="Meeting type" v={s.meetingType.replace(/_/g, ' ')} />}
          {s.meetingDate && <Row label="Meeting date" v={formatDateAU(s.meetingDate)} />}
          {s.declarationName && <Row label="Declared by" v={s.declarationName} />}
          {s.declarationAt && <Row label="Declared at" v={formatDateAU(s.declarationAt)} />}
          {s.skillVersion != null && <Row label="Compliance skill" v={`v${s.skillVersion}`} />}
          <Row
            label="Findings"
            v={
              findings.length === 0
                ? 'none'
                : `${findings.length} total${openFindings > 0 ? ` · ${openFindings} open` : ''}`
            }
          />
          <Row label="Sections" v={sections.length === 0 ? 'none' : `${sections.length}`} />
        </dl>
      </div>

      <div className="border-t border-hairline-paper px-4 py-2 flex items-center justify-between text-xs">
        <span className="text-ink-mute qa-mono">ref: {s.sourceReferenceId}</span>
        {s.pdfUrl ? (
          <a href={s.pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost">
            ↓ View PDF
          </a>
        ) : (
          <span className="text-ink-mute italic">PDF not attached</span>
        )}
      </div>

      {tab === 'unlinked' && (
        <div className="border-t border-hairline-paper px-4 py-3">
          {linking ? (
            <ClientPicker
              initialTerm={s.clientNameRaw ?? ''}
              busy={busy}
              onCancel={() => setLinking(false)}
              onPick={(c) => {
                if (
                  window.confirm(
                    `Link this file note to ${c.firstName} ${c.surname}? This stamps their file-note compliance date.`,
                  )
                ) {
                  onLink(c.id);
                }
              }}
            />
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="btn-primary text-sm"
                disabled={busy}
                onClick={() => setLinking(true)}
              >
                Link to a client
              </button>
              <button
                type="button"
                className="btn-ghost text-sm"
                disabled={busy}
                onClick={() => {
                  if (window.confirm('Dismiss this file note session as not-a-client-we-hold?')) {
                    onDismiss();
                  }
                }}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}

      {tab === 'dismissed' && (
        <div className="border-t border-hairline-paper px-4 py-3">
          <button
            type="button"
            className="btn-ghost text-sm"
            disabled={busy}
            onClick={onRestore}
          >
            Restore to queue
          </button>
        </div>
      )}
    </li>
  );
}

/* ------------------------------------------------------------- client picker */

function ClientPicker({
  initialTerm,
  busy,
  onPick,
  onCancel,
}: {
  initialTerm: string;
  busy: boolean;
  onPick: (c: ClientLite) => void;
  onCancel: () => void;
}) {
  const [term, setTerm] = useState(initialTerm);
  const [debounced, setDebounced] = useState(initialTerm);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), 250);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isFetching } = useQuery({
    queryKey: ['client-search', debounced],
    queryFn: () =>
      api<{ data: ClientLite[] }>(`/api/clients?q=${encodeURIComponent(debounced.trim())}&limit=8`),
    enabled: debounced.trim().length >= 2,
  });

  const results = data?.data ?? [];

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <input
          type="text"
          autoFocus
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Search clients by name or email…"
          className="flex-1 px-3 py-2 text-sm bg-parchment/40 border border-hairline-paper text-ink focus:outline-none focus:border-rust"
        />
        <button type="button" className="btn-ghost text-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>

      {debounced.trim().length < 2 ? (
        <p className="text-xs text-ink-mute">Type at least two characters to search.</p>
      ) : isFetching ? (
        <p className="text-xs text-ink-mute">Searching…</p>
      ) : results.length === 0 ? (
        <p className="text-xs text-ink-mute">
          No clients match “{debounced.trim()}”. If this person isn't on the books yet, dismiss the
          session instead.
        </p>
      ) : (
        <ul className="divide-y divide-hairline-paper border border-hairline-paper">
          {results.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="text-sm">
                <span className="text-ink">
                  {c.firstName} <em>{c.surname}</em>
                </span>
                <span className="text-ink-mute ml-2 qa-mono text-xs">
                  {[
                    c.dateOfBirth ? formatDateAU(c.dateOfBirth) : null,
                    c.email1,
                    [c.suburb, c.postcode].filter(Boolean).join(' '),
                  ]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </span>
              </div>
              <button
                type="button"
                className="btn-primary text-xs"
                disabled={busy}
                onClick={() => onPick(c)}
              >
                Link
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Row({ label, v }: { label: string; v: string }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2">
      <dt className="text-ink-mute qa-mono uppercase text-xs">{label}</dt>
      <dd className="text-ink">{v}</dd>
    </div>
  );
}
